<template>
  <table class="w-full border-separate" style="border-spacing: 0 0.5rem;">
    <thead>
      <tr>
        <!-- Status Indicator -->
        <th
          v-if="!isDeletedTab"
          class="directory-table__heading directory-table__heading--condensed no-wrap"
        >
          Output Status
        </th>

        <th
          v-if="isDeletedTab"
          class="directory-table__heading"
        >
          Permanent <span style="white-space:nowrap">Deletion In</span>
        </th>

        <th class="directory-table__heading">Title</th>

        <th v-if="hasTeams" class="directory-table__heading">Team</th>

        <th class="directory-table__heading tgbNoWrap">Modified</th>

        <th v-if="!hasTeams" class="directory-table__heading">Created</th>

        <th
          v-if="!isDeletedTab"
          class="directory-table__heading"
        >
          <span class="tgb-sr-only">Starred</span>
        </th>

        <!-- Context Menu -->
        <th class="directory-table__heading tgbNoWrap"></th>
      </tr>
    </thead>

    <tbody>
      <tr
        v-for="production in productions"
        :key="production._id"
        class="directory-table__row select-none"
        :data-test-id="production._id"
        data-entity="producer-dashboard-clickProductionRow"
        @click="$emit('dir-item-clicked', production)"
        @dblclick="$emit('dir-item-dblclicked', production)"
        @contextmenu.prevent="$emit('on-dir-context-menu', { e: $event, payload: production })"
        :class="{ 'directory-table__row--active': activeItem === production._id }"
      >
        <!-- Days until perma-delete -->
        <td
          v-if="isDeletedTab"
          data-test="producer-dashboard-table-cell-perma-delete"
          class="directory-table__cell directory-table__cell--perma-delete"
        >
          <span>{{ getDaysUntilDelete(production.deleted) }}</span>
        </td>

        <!-- Output Status Indicator -->
        <td
          v-if="!isDeletedTab"
          data-test="producer-dashboard-table-cell-output-status"
          class="directory-table__cell directory-table__cell--condensed text-center"
          :title="production.connected ? 'Active' : 'Inactive'"
        >
          <div
            data-test="producer-dashboard-table-cell-output-status-indicator"
            class="m-auto h-2 w-2 rounded-full"
            :class="production.connected ? 'bg-producer-slate-success' : 'bg-producer-slate-danger'"
          />
        </td>

        <!-- Production Name -->
        <td
          class="directory-table__cell directory-table__cell--title"
          data-test="producer-dashboard-table-cell-title"
        >
          <div class="flex items-center">
            <div
              class="mr-4 max-w-[33vw] truncate font-bold"
              :title="production.title"
            >
              {{ production.title }}
            </div>

            <!-- Users in the production -->
            <AvatarList
              v-if="!isDeletedTab"
              :users="getUsersInProduction(production)"
              :max="2"
            >
              <!-- Blank when no users -->
              <template #noUsers>&nbsp;</template>
            </AvatarList>
          </div>
        </td>

        <!-- Owner -->
        <td
          v-if="hasTeams"
          class="directory-table__cell directory-table__cell--team"
          data-test="producer-dashboard-table-cell-team"
        >
          <div class="flex items-center">
             <img
              v-if="production.ownedBy && production.ownedBy.profileImageUrl"
              class="tgbUserIconInline"
              :src="production.ownedBy.profileImageUrl"
              :alt="`Profile image of ${production.ownedBy.displayName}`"
              @error="$event.target.src=defaultAvatar"
              :title="production.ownedBy ? `${production.ownedBy.displayName} - ${formatDate(production.created)}` : ''"
            />
          </div>
        </td>

        <!-- Last Updated By -->
        <td
          class="directory-table__cell directory-table__cell--modified-by"
          data-test="producer-dashboard-table-cell-modified-by"
        >
          <div class="flex items-center">
            <div class="production-detail production-detail--group">
              <div>
                {{
                  production.modifiedBy && production.modifiedBy.displayName
                    ? production.modifiedBy.displayName
                    : "N/A"
                }}
              </div>

              <div>
                {{ timestamp(production.modified) }}
              </div>
            </div>
          </div>
        </td>

        <!-- Created By (only shown when no team) -->
        <td
          v-if="!hasTeams" class="directory-table__cell directory-table__cell--created-by"
          data-test="producer-dashboard-table-cell-created-by"
        >
          <div class="flex items-center">
             <img
              v-if="production.createdBy && production.createdBy.profileImageUrl"
              class="mr-4 h-7 w-7 rounded-full"
              :src="production.createdBy.profileImageUrl"
              :alt="`Profile image of ${production.createdBy.displayName}`"
              :title="production.createdBy ? `${production.createdBy.displayName} - ${formatDate(production.created)}` : ''"
              @error="$event.target.src=defaultAvatar"
            />
          </div>
        </td>

        <!-- Starred -->
        <td
          v-if="!isDeletedTab"
          class="directory-table__cell directory-table__cell--star"
          data-test="producer-dashboard-table-cell-star"
        >
          <label
            @click.prevent.stop="$emit('dir-item-starred', production)"
            class="mr-3 cursor-pointer"
          >
            <span class="tgb-sr-only">Star {{ production.title }}</span>

            <input
              type="checkbox"
              class="tgb-sr-only"
              :checked="production.flagged"
            />

            <span
              class="fa-star mb-1 text-producer-primary"
              :class="production.flagged ? 'fas' : 'far'"
            />
          </label>
        </td>


        <!-- Context Menu -->
        <td
          class="directory-table__cell directory-table__cell--condensed"
          data-test="producer-dashboard-table-cell-context-menu"
        >
          <dropdown position="bottom right" :classes="dropdownClasses">
            <template #trigger>
              <div
                class="tgb-icon-ellipsis-wrapper"
                data-entity="producer-dashboard-productionContext"
              >
                <i class="fas fa-ellipsis-h tgb-icon-ellipsis"></i>
              </div>
            </template>

            <template #dropdown-body>
              <ContextMenu :dropDownMenu="true" :actions="menuItems">
                <template v-slot="action">
                  <button
                    class="tgbContextMenuButton"
                    :data-entity="`producer-dashboard-${action.key}`"
                    @click="dropDownMenuItemClicked(action.callbackName, action.trigger, production)"
                  >
                    <span v-if="action.icon" class="fas" :class="action.icon"></span>
                    {{ action.text }}
                  </button>
                </template>
              </ContextMenu>
            </template>
          </dropdown>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import { mapGetters } from 'vuex';
import { format, formatDistance, addDays } from 'date-fns';
import { DefaultOptions, DeletedOptions } from './constants';
import { timestamp } from '../../utils/filters';

import AvatarList from '../user-avatar/AvatarList.vue';
import ContextMenu from '../context-menu/ContextMenu.vue';

export default {
  name: 'DirectoryTable',

  components: {
    AvatarList,
    ContextMenu,
  },

  props: {
    items: Array,
    activeItem: [Number, String],
    defaultAvatar: {
      type: String,
      default: './producer/assets/img/profile_placeholder.png',
    },
  },

  computed: {
    ...mapGetters(['hasTeams']),

    productions() {
      return this.items.map((item) => ({
        ...item,
        connected: this.isConnected(item),
      }));
    },

    isDeletedTab() {
      return this.$route.params?.tab === 'deleted';
    },

    menuItems() {
      return this.isDeletedTab ? DeletedOptions : DefaultOptions;
    },
  },

  data() {
    return {
      dropdownClasses: 'dropdown-menu-w-a dropdown-menu-p-0',
    };
  },

  methods: {
    timestamp,

    formatDate(ts) {
      return ts ? format(ts, 'MM/dd/yyyy') : 'N/A';
    },

    /**
    * Get list of users in the given production by ID.
    */
    getUsersInProduction(production) {
      const { byProduction } = this.$store.state.clients;
      const users = byProduction[production._id] || [];
      return users;
    },

    /**
    * Check all screens connected to the given production.
    * If any screen is currently open, then return true.
    * Otherwise return false.
    * @param {Object} production
    * @returns {Boolean}
    */
    isConnected(production) {
      // `screenMap` only contains connected screens,
      // so we just need to check length by production ID
      const { screenMap } = this.$store.state.screens;
      const screens = screenMap[production._id] || [];
      return screens.length;
    },

    getDaysUntilDelete(deleted) {
      const now = new Date();
      return deleted ? formatDistance(now, addDays(deleted, 30)) : '30 days';
    },

    dropDownMenuItemClicked(callbackName, trigger, item) {
      this.$emit('dropdown-menu-item-clicked', { callbackName, trigger, item});
    },
  },
};
</script>

<style scoped>
.directory-table__heading {
  font-family: "Barlow", sans-serif;
  font-style: normal;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 150%;
  padding: 0 0.5rem;
  color: #808689;
}

.directory-table__heading--condensed {
  padding: 0 1rem;
}

.directory-table__row {
  background-color: var(--tgb-surface-default-color);
  user-select: none;
}

.directory-table__row:hover {
  background-color: var(--tgb-directory-card-hover);
  color: #23cdf4;
  cursor: pointer;
}

.directory-table__row--active {
  background-color: var(--tgb-directory-card-hover);
  color: #23cdf4;
}

.tgbUserIconInline {
  width: 1.75rem;
  height: 1.75rem;
  margin-right: 1rem;
  border-radius: 100%;
}

.directory-table__cell {
  padding: 0.875rem 0.375rem;
  background-color: transparent;
  border-top: 1px solid transparent;
  border-bottom: 1px solid transparent;
}

.directory-table__row--active .directory-table__cell {
  border-top-color: #23cdf4;
  border-bottom-color: #23cdf4;
}

.directory-table__row--active .directory-table__cell:first-child {
  border-left-color: #23cdf4;
}

.directory-table__row--active .directory-table__cell:last-child {
  border-right-color: #23cdf4;
}

.directory-table__cell--condensed {
  width: 0;
}

.directory-table__cell--condensed:first-child {
  padding: 0.875rem 0.375rem 0.875rem 0.5rem;
  border-top-left-radius: var(--tgb-border-radius-default);
  border-bottom-left-radius: var(--tgb-border-radius-default);
  border-left: 1px solid transparent;
}

.directory-table__cell--condensed:last-child {
  padding-right: 0.7813rem;
  border-top-right-radius: var(--tgb-border-radius-default);
  border-bottom-right-radius: var(--tgb-border-radius-default);
  border-right: 1px solid transparent;
}

.directory-table__cell--team {
  width: 3.125rem;
  max-width: 3.125rem;
}

.directory-table__cell--modified-by,
.directory-table__cell--created-by {
  width: 12.5rem;
  max-width: 12.5rem;
}

.directory-table__cell-status-indicator {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 100%;
  background-color: #00D483;
  margin: auto;
}

.directory-table__cell-status-indicator--disconnected {
  background-color: #e65447;
}

.directory-table__cell-title {
  max-width: 33vw;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.production-detail--group :last-child {
  font-size: 0.6875rem;
}

.fa-star {
  margin-bottom: 0.25rem;
}

.fas.fa-star {
  color: #23CDF4;
}
</style>
